import React, { useState } from 'react';
import { Modal } from '../common/Modal';
import { motion } from 'framer-motion';
import { Upload, FileText, X, Import as Passport, Stamp, User, AlertTriangle, Eye, Check, CheckCircle } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import toast from 'react-hot-toast';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  pilgrimId: string;
  pilgrimName: string;
  onUpload: (pilgrimId: string, documentType: 'passport' | 'visa', file: File) => Promise<void>;
};

export const UploadDocumentModal = ({ isOpen, onClose, pilgrimId, pilgrimName, onUpload }: Props) => {
  const { translations } = useLanguage();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState<'passport' | 'visa'>('passport');
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [showExamples, setShowExamples] = useState(false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      validateAndSetFile(event.target.files[0]);
    }
  };

  const validateAndSetFile = (file: File) => {
    // Check file type
    if (!file.type.match('image/*') && !file.type.match('application/pdf')) {
      toast.error('Only image and PDF files are accepted');
      return;
    }

    // Check file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setSelectedFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }

    try {
      setIsUploading(true);
      await onUpload(pilgrimId, documentType, selectedFile);
      setSelectedFile(null);
    } catch (error) {
      console.error('Error uploading document:', error);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Upload Document for ${pilgrimName}`}
    >
      <div className="space-y-6">
        {/* Instructions */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-blue-600 mt-0.5 shrink-0" size={20} />
            <div>
              <h3 className="font-medium text-blue-800 mb-2 flex items-center justify-between">
                <span>Upload Instructions</span>
                <button 
                  className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  onClick={() => setShowExamples(!showExamples)}
                >
                  {showExamples ? 'Hide Examples' : 'Show Examples'}
                  {showExamples ? <X size={12} /> : <Eye size={12} />}
                </button>
              </h3>
              
              <div className="text-sm text-blue-700 space-y-2">
                <p className="font-medium">Requirements:</p>
                <ul className="list-disc list-inside space-y-1.5">
                  <li>Upload ONLY the pilgrim's document (not multiple documents in one image)</li>
                  <li>Upload a clear, high-quality image showing all details</li>
                  <li>Make sure the entire document is visible in the frame</li>
                  <li>Ensure all text is legible and not blurry</li>
                  <li>Maximum file size: 5MB</li>
                  <li>Accepted formats: JPEG, PNG, PDF</li>
                </ul>

                <p className="font-medium mt-3 border-t border-blue-200 pt-2">الإرشادات باللغة العربية:</p>
                <ul className="list-disc list-inside space-y-1.5">
                  <li>قم بتحميل وثيقة الحاج فقط (وليس عدة وثائق في صورة واحدة)</li>
                  <li>قم بتحميل صورة واضحة عالية الجودة تُظهر جميع التفاصيل</li>
                  <li>تأكد من أن الوثيقة بالكامل مرئية في الإطار</li>
                  <li>تأكد من أن جميع النصوص مقروءة وغير ضبابية</li>
                  <li>الحد الأقصى لحجم الملف: 5 ميجابايت</li>
                  <li>الصيغ المقبولة: JPEG، PNG، PDF</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Examples Section - Togglable */}
          {showExamples && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 pt-3 border-t border-blue-200"
            >
              <h4 className="text-sm font-medium text-blue-800 mb-2">Examples / صور توضيحية</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Good Example */}
                <div className="bg-white p-3 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle size={16} className="text-green-600" />
                    <h5 className="text-sm font-medium text-green-800">Good Example / مثال جيد</h5>
                  </div>
                  <div className="rounded overflow-hidden border border-gray-200">
                    <img 
                      src="https://i.ibb.co/NR1cwX3/Chat-GPT-Image-Apr-14-2025-12-50-54-AM.png" 
                      alt="Good passport sample" 
                      className="w-full h-auto"
                    />
                  </div>
                  <ul className="mt-2 text-xs text-green-700 list-disc list-inside">
                    <li>Clear, focused image</li>
                    <li>All edges visible</li>
                    <li>Good lighting</li>
                  </ul>
                </div>

                {/* Bad Example */}
                <div className="bg-white p-3 rounded-lg border border-red-200">
                  <div className="flex items-center gap-2 mb-2">
                    <X size={16} className="text-red-600" />
                    <h5 className="text-sm font-medium text-red-800">Bad Example / مثال سيء</h5>
                  </div>
                  <div className="rounded overflow-hidden border border-gray-200 bg-gray-100 flex items-center justify-center" style={{height: "150px"}}>
                    <div className="p-4 text-center">
                      <div className="relative w-full h-32 bg-gray-200 flex items-center justify-center rounded opacity-60">
                        <img 
                          src="https://i.ibb.co/HfHW6t2M/Chat-GPT-Image-Apr-14-2025-12-47-41-AM.png" 
                          alt="Bad passport sample" 
                          className="w-full h-full object-cover blur-sm"
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <X size={40} className="text-red-500" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <ul className="mt-2 text-xs text-red-700 list-disc list-inside">
                    <li>Blurry or unclear image</li>
                    <li>Poor lighting or shadows</li>
                    <li>Multiple documents in one image</li>
                  </ul>
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Document Type Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Document Type
          </label>
          <div className="grid grid-cols-2 gap-4">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                documentType === 'passport'
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-primary-200'
              }`}
              onClick={() => setDocumentType('passport')}
            >
              <div className="flex flex-col items-center gap-2">
                <div className={`p-3 rounded-full ${
                  documentType === 'passport' ? 'bg-primary-100' : 'bg-gray-100'
                }`}>
                  <Passport
                    size={24}
                    className={documentType === 'passport' ? 'text-primary-600' : 'text-gray-500'}
                  />
                </div>
                <span className={`font-medium ${
                  documentType === 'passport' ? 'text-primary-700' : 'text-gray-700'
                }`}>Passport</span>
              </div>
            </motion.div>
            
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                documentType === 'visa'
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-primary-200'
              }`}
              onClick={() => setDocumentType('visa')}
            >
              <div className="flex flex-col items-center gap-2">
                <div className={`p-3 rounded-full ${
                  documentType === 'visa' ? 'bg-primary-100' : 'bg-gray-100'
                }`}>
                  <Stamp
                    size={24}
                    className={documentType === 'visa' ? 'text-primary-600' : 'text-gray-500'}
                  />
                </div>
                <span className={`font-medium ${
                  documentType === 'visa' ? 'text-primary-700' : 'text-gray-700'
                }`}>Visa</span>
              </div>
            </motion.div>
          </div>
        </div>

        {/* File Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Upload Document
          </label>
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive 
                ? 'border-primary-500 bg-primary-50' 
                : selectedFile 
                  ? 'border-green-500 bg-green-50' 
                  : 'border-gray-300 hover:border-primary-300'
            }`}
          >
            {selectedFile ? (
              <div className="space-y-2">
                <div className="flex items-center justify-center">
                  <div className="bg-green-100 p-3 rounded-full">
                    <FileText className="h-6 w-6 text-green-600" />
                  </div>
                </div>
                <div className="text-green-600 font-medium">{selectedFile.name}</div>
                <div className="text-sm text-gray-500">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </div>
                <button
                  onClick={() => setSelectedFile(null)}
                  className="inline-flex items-center text-sm text-red-600 hover:text-red-800"
                >
                  <X size={16} className="mr-1" />
                  <span>Remove</span>
                </button>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <div className="mx-auto h-12 w-12 text-gray-400">
                    <Upload className="mx-auto h-full w-full" />
                  </div>
                  <div className="mt-2">
                    <label
                      htmlFor="file-upload"
                      className="cursor-pointer text-primary-600 hover:text-primary-700"
                    >
                      Upload a file
                    </label>{' '}
                    or drag and drop
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      className="sr-only"
                      accept="image/*,application/pdf"
                      onChange={handleFileChange}
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    PNG, JPG, or PDF up to 5MB
                  </p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            disabled={!selectedFile || isUploading}
            onClick={handleSubmit}
            className={`inline-flex items-center px-6 py-2 rounded-lg text-white 
              ${
                !selectedFile || isUploading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-primary-500 hover:bg-primary-600'
              }`}
          >
            {isUploading ? (
              <>
                <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                <span>Uploading...</span>
              </>
            ) : (
              <>
                <Upload size={20} className="mr-2" />
                <span>Upload Document</span>
              </>
            )}
          </motion.button>
        </div>
      </div>
    </Modal>
  );
};